import React, {
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useFileUpload } from '../hooks/offline-hooks/addFile';
import { useLocation } from 'react-router-dom';
import ReminderModal from '../components/modals/provider_modals/ReminderModal';

const MAX_TIME = 600;
const CHUNK_DURATION_MS = MAX_TIME * 1000;

const RecordingContext = createContext();

export function RecordingProvider({ children }) {
  const [isRecording, setIsRecording] = useState(false);
  const [loadingRecorder, setLoadingRecorder] = useState(false);
  const isRecordingRef = useRef(false);

  const setIsRecordingSafe = (val) => {
    setIsRecording(val);
    isRecordingRef.current = val;
  };

  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const chunkTimerRef = useRef(null);
  const [audioURL, setAudioURL] = useState(null);
  const [ready, setReady] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const location = useLocation();
  const recordingUrl = useRef(null);
  const [intervalId, setIntervalId] = useState(null);
  const fullAudioFileRef = useRef([]);
  const { addFileToS3 } = useFileUpload();
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isClearVoice, setIsClearVoice] = useState(false);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const noiseFloorRef = useRef(null);
  const telephonyModeRef = useRef(false);
  const [voiceLevel, setVoiceLevel] = useState(0);
  const [activityLevel, setActivityLevel] = useState(0);
  const rmsEmaRef = useRef(0);
  const lastUpdateRef = useRef(0);
  const sessionIdRef = useRef(null);
  const chunkIndexRef = useRef(0);
  const uploadedChunkIdsRef = useRef(new Set());
  const lastUploadPromiseRef = useRef(Promise.resolve());

  const startVoiceMonitor = (stream) => {
    const AudioContextClass =
      window.AudioContext || window['webkitAudioContext'];
    if (!AudioContextClass) {
      console.warn('Web Audio API not supported in this browser');
      return;
    }
    const ctx = new AudioContextClass();
    audioCtxRef.current = ctx;

    const src = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.8;
    src.connect(analyser);
    analyserRef.current = analyser;

    const track = stream.getAudioTracks()[0];
    let sr = ctx.sampleRate;
    try {
      const s = track && track.getSettings ? track.getSettings() : {};
      if (s && s.sampleRate) sr = s.sampleRate;
    } catch (e) {}
    telephonyModeRef.current = !!(sr && sr < 32000);

    const buf = new Float32Array(analyser.fftSize);

    const tick = () => {
      analyser.getFloatTimeDomainData(buf);
      let sum = 0,
        peak = 0,
        crossings = 0;
      let prev = buf[0];
      for (let i = 0; i < buf.length; i++) {
        const v = buf[i];
        sum += v * v;
        const a = Math.abs(v);
        if (a > peak) peak = a;
        if ((prev <= 0 && v > 0) || (prev >= 0 && v < 0)) crossings++;
        prev = v;
      }
      const rms = Math.sqrt(sum / buf.length);
      const zcr = crossings / buf.length;
      const clipping = peak > 0.98;

      if (noiseFloorRef.current == null)
        noiseFloorRef.current = Math.max(0.001, rms);
      const speechLikeEnergy = rms > noiseFloorRef.current * 2.5;
      const alpha = speechLikeEnergy ? 0.005 : 0.1;
      noiseFloorRef.current = noiseFloorRef.current * (1 - alpha) + rms * alpha;

      const speaking = speechLikeEnergy && zcr > 0.02 && zcr < 0.3;

      if (!rmsEmaRef.current) rmsEmaRef.current = rms;
      rmsEmaRef.current = rmsEmaRef.current * 0.8 + rms * 0.2;

      const floor = Math.max(1e-6, noiseFloorRef.current || rmsEmaRef.current);
      const norm = Math.max(0, (rms - floor) / (floor * 4 + 1e-6));
      const level = Math.min(1, norm);
      const fullScale = floor * 5;
      const totalActivity = Math.min(1, rmsEmaRef.current / fullScale);
      const now = performance.now();
      if (now - lastUpdateRef.current > 120) {
        setIsSpeaking(speaking);
        setIsClearVoice(speaking && !clipping && !telephonyModeRef.current);
        setVoiceLevel(level);
        setActivityLevel(totalActivity);
        lastUpdateRef.current = now;
      }

      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  const stopVoiceMonitor = async () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    try {
      analyserRef.current && analyserRef.current.disconnect();
    } catch (e) {}
    try {
      audioCtxRef.current && (await audioCtxRef.current.close());
    } catch (e) {}
    analyserRef.current = null;
    audioCtxRef.current = null;
    noiseFloorRef.current = null;
    telephonyModeRef.current = false;
    setIsSpeaking(false);
    setIsClearVoice(false);
    setVoiceLevel(0);
    setActivityLevel(0);
    rmsEmaRef.current = 0;
  };

  const uploadChunk = async (blob, chunkId) => {
    if (!blob || blob.size === 0) return;

    setLoadingRecorder(true);

    const file = new File([blob], `audio_chunk_${chunkId}.webm`, {
      type: 'audio/webm',
    });

    await addFileToS3({
      file,
      onSubmit: ({ url, name, extension, size }) => {
        setAudioURL({ url, name, extension, size });
      },
      setLoading: setLoadingRecorder,
    });

    setReady(true);
  };

  const startChunk = () => {
    if (chunkTimerRef.current) clearTimeout(chunkTimerRef.current);

    const recorder = new MediaRecorder(mediaStreamRef.current, {
      mimeType: 'audio/webm;codecs=opus',
      audioBitsPerSecond: 160000,
    });
    mediaRecorderRef.current = recorder;

    const chunks = [];
    const chunkId = `${sessionIdRef.current}_${chunkIndexRef.current++}`;

    recorder.ondataavailable = (e) => {
      if (e.data?.size > 0) {
        chunks.push(e.data);
        fullAudioFileRef.current.push(e.data);
      }
    };

    recorder.onstop = () => {
      if (uploadedChunkIdsRef.current.has(chunkId)) return;
      uploadedChunkIdsRef.current.add(chunkId);

      const blob = new Blob(chunks, {
        type: recorder.mimeType || 'audio/webm',
      });

      lastUploadPromiseRef.current = (async () => {
        await uploadChunk(blob, chunkId);
      })();

      if (isRecordingRef.current) startChunk();
    };

    recorder.start();

    chunkTimerRef.current = setTimeout(() => {
      if (recorder.state === 'recording') recorder.stop();
    }, CHUNK_DURATION_MS);
  };

  const startRecording = async () => {
    sessionIdRef.current = crypto.randomUUID?.() || String(Date.now());
    chunkIndexRef.current = 0;
    uploadedChunkIdsRef.current = new Set();

    recordingUrl.current = location.pathname;

    if (intervalId) clearInterval(intervalId);
    setSeconds(0);
    const id = setInterval(() => setSeconds((s) => s + 1), 1000);
    setIntervalId(id);

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    mediaStreamRef.current = stream;

    setIsRecordingSafe(true);
    setIsRecording(true);

    startVoiceMonitor(stream);
    startChunk();
  };

  const stopRecording = async () => {
    setIsRecordingSafe(false);
    setIsRecording(false);

    if (chunkTimerRef.current) clearTimeout(chunkTimerRef.current);

    const recorder = mediaRecorderRef.current;

    if (recorder && recorder.state === 'recording') {
      recorder.stop();
    }

    await new Promise((r) => setTimeout(r, 0));

    try {
      await lastUploadPromiseRef.current;
    } catch (e) {
      console.error('Final chunk upload failed', e);
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => t.stop());
    }

    if (intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }

    await stopVoiceMonitor();
  };
  const value = useMemo(
    () => ({
      isRecording,
      startRecording,
      stopRecording,
      loadingRecorder,
      audioURL,
      setAudioURL,
      ready,
      setReady,
      seconds,
      fullAudioFileRef,
      recordingUrl,
      isSpeaking,
      isClearVoice,
      voiceLevel,
      activityLevel,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isRecording,
      loadingRecorder,
      audioURL,
      ready,
      seconds,
      isSpeaking,
      isClearVoice,
      voiceLevel,
      activityLevel,
    ],
  );
  return (
    <RecordingContext.Provider value={value}>
      {children}
      <ReminderModal
        title="Recording in Progress"
        show={isRecording}
        url={recordingUrl.current}
        timer={seconds}
      />
    </RecordingContext.Provider>
  );
}

export const useVoiceRecorder = () => useContext(RecordingContext);
